import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Heart, UserCircle2, Shield } from 'lucide-react';
import { BUSINESS } from '../config';
import { useAuth } from '../context/AuthContext';
import CartIcon from './CartIcon';
import VacationBanner from './VacationBanner';

export default function Header({ maintenance }) {
  const location = useLocation();
  const { user } = useAuth();

  // « Catalogue » retiré du menu (2026-06-01) : Neufs / Reconditionnés sont les 2 vraies
  // portes d'entrée (l'état est ensuite re-basculable via le filtre côté catalogue).
  // « Deals » retiré du menu (2026-06-02) : la feature deals groupés n'est pas
  // encore opérationnelle. On garde une carte teaser « À venir » non cliquable
  // sur l'accueil (cf Home.js), mais plus aucune porte d'entrée cliquable vers /deals.
  const links = [
    { to: '/', label: 'Accueil' },
    { to: '/catalogue?condition=neuf', label: 'Neufs' },
    { to: '/catalogue?condition=reconditionne', label: 'Reconditionnés' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => {
    const [basePath, qs] = path.split('?');
    if (basePath !== location.pathname) return false;
    if (!qs) return !location.search;
    return location.search.includes(qs);
  };

  const accountLink = user ? '/compte' : '/compte/inscription';

  return (
    <header className="bg-primary text-white shadow-lg sticky top-0 z-50" data-testid="header">
      {/* Bandeau de congés : INDÉPENDANT de maintenance.enabled, qui masque le
          bouton « Ajouter au panier ». Pendant les congés on continue de vendre,
          seule l'expédition est différée. Ne pas fusionner les deux bandeaux. */}
      <VacationBanner />

      {maintenance?.enabled && (
        <div className="bg-white/5 border-b border-white/15" data-testid="maintenance-banner">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2">
            <p className="font-accent text-[11px] sm:text-xs text-slate-300 text-center uppercase tracking-widest">{maintenance.message}</p>
          </div>
        </div>
      )}

      {/* Top bar cyan : USP boutique + tel */}
      <div className="bg-secondary py-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex justify-between items-center text-sm font-medium gap-3">
          <span className="font-accent uppercase tracking-wide text-xs sm:text-sm">
            <span className="hidden sm:inline">Boutique en ligne · PC neufs &amp; reconditionnés · Retrait à Meaux</span>
            <span className="sm:hidden">Boutique en ligne</span>
          </span>
          <a
            href={`tel:${BUSINESS.telephoneRaw}`}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-1 rounded-sm transition-colors font-bold active:scale-95 whitespace-nowrap"
            data-testid="header-phone-link"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden xs:inline">{BUSINESS.telephone}</span>
            <span className="xs:hidden text-xs">{BUSINESS.telephone}</span>
          </a>
        </div>
      </div>

      {/* Nav principale */}
      <nav className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img
              src="/logo-meteris.png"
              alt="Météris"
              className="h-9 w-9 rounded-full object-cover"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {links.map((link) => (
              <Link
                key={link.to + link.label}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive(link.to) ? 'text-secondary' : 'text-white hover:text-secondary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Groupe d'actions : visible dès `md`, comme son conteneur. Ses enfants
              étaient tous gatés à `lg` alors que le bloc mobile s'arrête à `md` et que
              `.mobile-nav` est masquée par la même media query — entre 768 et 1023 px,
              la tablette, il ne restait donc AUCUNE icône panier, favoris ni compte.
              Le groupe entier descend à `md` (une icône panier isolée avait été
              refusée en revue comme artefact visuel) ; seuls les LIBELLÉS se replient
              sous `lg`, pour tenir dans la largeur d'une tablette. Chaque lien porte
              donc un `aria-label` : sans lui, un libellé replié laisse un lien anonyme
              pour un lecteur d'écran. */}
          <div className="hidden md:flex items-center gap-2 lg:gap-5" data-testid="header-actions">
            <Link
              to="/compte/favoris"
              aria-label="Favoris"
              data-testid="header-favoris"
              className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-white/90 hover:text-secondary transition-colors"
            >
              <Heart className="w-4 h-4" />
              <span className="hidden lg:inline">Favoris</span>
            </Link>
            <CartIcon
              className="flex text-white/90 hover:text-secondary transition-colors"
              iconClassName="w-4 h-4"
            />
            <Link
              to={accountLink}
              aria-label={user ? 'Mon compte' : 'Se connecter'}
              data-testid="header-compte"
              className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-white/90 hover:text-secondary transition-colors"
            >
              <UserCircle2 className="w-4 h-4" />
              <span className="hidden lg:inline">{user ? 'Mon compte' : 'Se connecter'}</span>
            </Link>

            {user?.can_access_backoffice && (
              <Link
                to="/my/dashboard"
                aria-label="Admin"
                data-testid="header-admin"
                className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-white/90 hover:text-secondary transition-colors"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden lg:inline">Admin</span>
              </Link>
            )}

          </div>

          {/* Mobile : accès compte (le bouton « Réserver » du header a été retiré 2026-06-01
              — la réservation se fait par produit, pas globalement). */}
          <div className="flex md:hidden items-center gap-2" data-testid="header-mobile-actions">
            <CartIcon className="text-white/90 hover:text-secondary" />
            <Link
              to={accountLink}
              aria-label={user ? 'Mon compte' : 'Se connecter'}
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-secondary text-xs font-semibold uppercase tracking-wider"
            >
              <UserCircle2 className="w-5 h-5" />
              <span>{user ? 'Compte' : 'Connexion'}</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile nav horizontale scrollable, visible en permanence */}
      <nav className="mobile-nav" aria-label="Navigation mobile">
        {links.map((link) => (
          <Link
            key={'mob-' + link.to + link.label}
            to={link.to}
            className={isActive(link.to) ? 'active' : ''}
          >
            {link.label}
          </Link>
        ))}
        <Link to="/compte/favoris">Favoris</Link>
        <Link to={accountLink}>{user ? 'Mon compte' : 'Se connecter'}</Link>
        {user?.can_access_backoffice && <Link to="/my/dashboard">Admin</Link>}
      </nav>
    </header>
  );
}
